import { useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  FileUp,
  FileText,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Sparkles,
  X,
} from 'lucide-react';
import { api, streamSse } from '@/lib/api';
import { Modal } from '@/components/ui/modal';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/toast';
import type {
  DocKickoffEvent,
  DocKickoffUploadResp,
  DocPhase,
} from '@/pages/radar/types';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type Step = 'pick' | 'run';

const ACCEPT = '.txt,.md,.markdown,.docx,.doc';
const MAX_BYTES = 5 * 1024 * 1024;

const PHASE_LABEL: Record<DocPhase, string> = {
  idle: '等待开始',
  parsing: '解析文档',
  metadata: '提取 IP 元数据',
  episodes: '识别分集结构',
  scenes: '提取分集场景',
  atlas: '提取故事图谱',
  done: '完成',
};

const PHASE_ORDER: DocPhase[] = [
  'parsing',
  'metadata',
  'episodes',
  'scenes',
  'atlas',
  'done',
];

export function DocumentImportDialog({ open, onOpenChange }: Props) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [step, setStep] = useState<Step>('pick');
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [upload, setUpload] = useState<DocKickoffUploadResp | null>(null);

  const [phase, setPhase] = useState<DocPhase>('idle');
  const [ipName, setIpName] = useState<string>('');
  const [epCount, setEpCount] = useState<number>(0);
  const [epDone, setEpDone] = useState<number>(0);
  const [atlasDone, setAtlasDone] = useState<number>(0);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [doneEp, setDoneEp] = useState<string | null>(null);
  const [running, setRunning] = useState(false);

  const ctrlRef = useRef<AbortController | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  /** 关闭 Dialog 时彻底清理(若正在跑则尝试 cancel) */
  const cleanup = () => {
    if (ctrlRef.current) {
      try {
        ctrlRef.current.abort();
      } catch {
        /* ignore */
      }
      ctrlRef.current = null;
    }
    if (upload?.docId && running) {
      // 后端非阻塞 cancel(不等待返回)
      api.post(`document-kickoff/${upload.docId}/cancel`).json().catch(() => {});
    }
  };

  const resetAll = () => {
    cleanup();
    setStep('pick');
    setFile(null);
    setUpload(null);
    setUploading(false);
    setPhase('idle');
    setIpName('');
    setEpCount(0);
    setEpDone(0);
    setAtlasDone(0);
    setErrorMsg(null);
    setDoneEp(null);
    setRunning(false);
  };

  // open=false → 清理(有错误态时延长保留,避免用户切回来什么都看不到)
  useEffect(() => {
    if (!open) {
      const delay = errorMsg ? 30_000 : 200;
      const t = setTimeout(resetAll, delay);
      return () => clearTimeout(t);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, errorMsg]);

  // 卸载时同步 abort,避免泄漏
  useEffect(() => {
    return () => {
      if (ctrlRef.current) {
        try {
          ctrlRef.current.abort();
        } catch {
          /* ignore */
        }
      }
    };
  }, []);

  const handlePickFile = (f: File | null) => {
    if (!f) return;
    if (f.size > MAX_BYTES) {
      toast.error('文件超过 5MB', '请压缩或拆分后重试');
      return;
    }
    const ext = f.name.toLowerCase().split('.').pop() || '';
    if (!['txt', 'md', 'markdown', 'docx', 'doc'].includes(ext)) {
      toast.error('不支持的格式', '仅支持 .txt / .md / .docx / .doc');
      return;
    }
    setFile(f);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const resp = await api
        .post('document-kickoff/upload', { body: fd, timeout: 120_000 })
        .json<DocKickoffUploadResp>();
      setUpload(resp);
      setStep('run');
      // 上传成功立刻自动开跑
      void startStream(resp.docId);
    } catch (err: unknown) {
      const msg =
        err instanceof Error
          ? err.message.replace(/^HTTP \d+:\s*/, '')
          : '上传失败';
      toast.error('上传失败', msg.slice(0, 200) || '稍后重试');
    } finally {
      setUploading(false);
    }
  };

  const startStream = (docId: string) => {
    setRunning(true);
    setErrorMsg(null);
    setPhase('parsing');
    const ctrl = streamSse<DocKickoffEvent>(
      `document-kickoff/${docId}/run`,
      { method: 'POST' },
      (ev) => {
        if (ev.event === 'phase') {
          setPhase(ev.phase);
        } else if (ev.event === 'ip_seed_done') {
          setIpName(ev.ipSeed?.ipName || '');
        } else if (ev.event === 'episodes_outlined') {
          setEpCount(ev.count);
        } else if (ev.event === 'episode_scene_done') {
          setEpDone(ev.done);
          setEpCount(ev.total);
        } else if (ev.event === 'atlas_tab_done') {
          setAtlasDone((n) => n + 1);
        } else if (ev.event === 'done') {
          setPhase('done');
          setDoneEp(ev.episodeId);
          setRunning(false);
          qc.invalidateQueries({ queryKey: ['writer', 'projects'] });
          toast.success('提取完成', '可在项目大厅打开');
        } else if (ev.event === 'error') {
          setErrorMsg(ev.message || '提取失败');
          setRunning(false);
        }
      },
      (err) => {
        setErrorMsg(err instanceof Error ? err.message : '网络错误');
        setRunning(false);
      },
    );
    ctrlRef.current = ctrl;
  };

  const phaseIdx = PHASE_ORDER.indexOf(phase);
  const passed = (p: DocPhase) => PHASE_ORDER.indexOf(p) < phaseIdx;
  const active = (p: DocPhase) => phase === p;
  const isDone = phase === 'done' && Boolean(doneEp);

  return (
    <Modal
      open={open}
      onOpenChange={(o) => {
        if (!o && running && !errorMsg && !isDone) {
          // 运行中关闭 → 等用户确认
          if (!window.confirm('Agent 正在解析,关闭将取消本次任务?')) return;
        }
        if (!o && errorMsg && !isDone) {
          // 错误态关闭 → 确认一次,避免误触把错误信息冲掉
          if (
            !window.confirm(
              `提取失败:${errorMsg.slice(0, 80)}\n\n确定关闭?(可点「重试」继续)`,
            )
          )
            return;
        }
        onOpenChange(o);
      }}
      title="导入文档立项"
      description={
        step === 'pick'
          ? '上传 .txt / .md / .docx / .doc · 系统会从原文中提取 IP 包并落地为可微调的剧集'
          : '请勿关闭窗口 · Agent 正在逐集提取场景与故事图谱'
      }
      className="max-w-xl"
      footer={
        step === 'pick' ? (
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onOpenChange(false)}
              disabled={uploading}
            >
              取消
            </Button>
            <Button
              variant="agent"
              agentColor="writer"
              size="sm"
              onClick={handleUpload}
              disabled={!file || uploading}
            >
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> 上传中…
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4" /> 开始解析
                </>
              )}
            </Button>
          </>
        ) : (
          <>
            {errorMsg && upload && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => startStream(upload.docId)}
              >
                重试
              </Button>
            )}
            <Button
              variant={isDone ? 'agent' : 'ghost'}
              agentColor={isDone ? 'writer' : undefined}
              size="sm"
              onClick={() => {
                if (isDone && doneEp) {
                  onOpenChange(false);
                  navigate({
                    to: '/writer/$episodeId' as never,
                    params: { episodeId: doneEp } as never,
                  });
                  return;
                }
                onOpenChange(false);
              }}
              disabled={running && !errorMsg}
            >
              {isDone ? '前往工作台' : running ? '运行中' : '关闭'}
            </Button>
          </>
        )
      }
    >
      {step === 'pick' ? (
        <PickStep
          file={file}
          onPickFile={handlePickFile}
          fileInputRef={fileInputRef}
        />
      ) : (
        <RunStep
          filename={upload?.filename ?? ''}
          charCount={upload?.charCount ?? 0}
          ipName={ipName}
          epDone={epDone}
          epCount={epCount}
          atlasDone={atlasDone}
          errorMsg={errorMsg}
          isDone={isDone}
          passed={passed}
          active={active}
        />
      )}
    </Modal>
  );
}

function PickStep({
  file,
  onPickFile,
  fileInputRef,
}: {
  file: File | null;
  onPickFile: (f: File | null) => void;
  fileInputRef: React.MutableRefObject<HTMLInputElement | null>;
}) {
  const [dragOver, setDragOver] = useState(false);
  return (
    <div className="space-y-3 pb-1">
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT}
        className="hidden"
        onChange={(e) => onPickFile(e.target.files?.[0] ?? null)}
      />
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          const f = e.dataTransfer.files?.[0];
          if (f) onPickFile(f);
        }}
        className={
          'flex w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-6 py-10 text-sm transition ' +
          (dragOver
            ? 'border-brand-500 bg-brand-50/40 text-brand-700'
            : 'border-outline-variant/60 bg-surface-variant/30 text-neutral-500 hover:border-brand-300 hover:bg-brand-50/20')
        }
      >
        <FileUp className="h-7 w-7" />
        <div className="font-medium text-neutral-800">
          {file ? '更换文件' : '点击或拖拽文件到此处上传'}
        </div>
        <div className="text-[11px] text-neutral-500">
          支持 .txt / .md / .docx / .doc · 单文件 ≤ 5MB
        </div>
      </button>
      {file && (
        <div className="flex items-center gap-2 rounded-lg border border-outline-variant/40 bg-surface px-3 py-2 text-xs">
          <FileText className="h-4 w-4 shrink-0 text-brand-500" />
          <div className="min-w-0 flex-1">
            <div className="truncate font-medium text-neutral-800">
              {file.name}
            </div>
            <div className="text-[11px] text-neutral-500">
              {(file.size / 1024).toFixed(1)} KB
            </div>
          </div>
          <button
            type="button"
            onClick={() => onPickFile(null)}
            className="text-neutral-400 hover:text-neutral-700"
            aria-label="移除"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      <div className="rounded-lg bg-surface-variant/40 px-3 py-2 text-[11px] leading-relaxed text-neutral-500">
        系统会按原文实际章节自动切分集数,严格忠于原文 · 不会二次创作或脑补。
      </div>
    </div>
  );
}

function RunStep({
  filename,
  charCount,
  ipName,
  epDone,
  epCount,
  atlasDone,
  errorMsg,
  isDone,
  passed,
  active,
}: {
  filename: string;
  charCount: number;
  ipName: string;
  epDone: number;
  epCount: number;
  atlasDone: number;
  errorMsg: string | null;
  isDone: boolean;
  passed: (p: DocPhase) => boolean;
  active: (p: DocPhase) => boolean;
}) {
  const rows: { key: DocPhase; label: string; detail: string }[] = [
    { key: 'parsing', label: '解析文档', detail: `${(charCount / 1000).toFixed(1)} 千字 · ${filename}` },
    { key: 'metadata', label: '提取 IP 元数据', detail: ipName ? `「${ipName}」` : '提取 IP 名称 / 主角 / 冲突 / 爽点' },
    { key: 'episodes', label: '识别分集结构', detail: epCount > 0 ? `${epCount} 集` : '按原文章节切分' },
    { key: 'scenes', label: '提取分集场景', detail: epCount > 0 ? `${epDone} / ${epCount}` : '逐集 LLM 抽取' },
    { key: 'atlas', label: '提取故事图谱', detail: `${atlasDone} / 9 tab` },
  ];

  return (
    <div className="space-y-3 pb-1">
      <ul className="space-y-2">
        {rows.map((r) => {
          const done = passed(r.key) || (isDone && r.key !== 'done');
          const isActive = active(r.key) && !isDone;
          return (
            <li
              key={r.key}
              className="flex items-center gap-3 rounded-lg border border-outline-variant/40 bg-surface px-3 py-2.5"
            >
              <div className="flex h-7 w-7 shrink-0 items-center justify-center">
                {done ? (
                  <CheckCircle2 className="h-5 w-5 text-success" />
                ) : isActive ? (
                  <Loader2 className="h-5 w-5 animate-spin text-brand-500" />
                ) : (
                  <div className="h-2 w-2 rounded-full bg-outline" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div
                  className={
                    'text-sm ' +
                    (done
                      ? 'text-neutral-900'
                      : isActive
                      ? 'font-medium text-brand-700'
                      : 'text-neutral-500')
                  }
                >
                  {PHASE_LABEL[r.key] ?? r.label}
                </div>
                <div className="truncate text-[11px] text-neutral-500">
                  {r.detail}
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      {isDone && (
        <div className="flex items-start gap-2 rounded-lg bg-success-soft/40 px-3 py-2 text-xs leading-relaxed text-success">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            提取完成 · 已生成 SsmlDoc + Story Atlas,在工作台二次确认即可初始化
          </div>
        </div>
      )}

      {errorMsg && (
        <div className="flex items-start gap-2 rounded-lg bg-danger-soft/40 px-3 py-2 text-xs leading-relaxed text-danger">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <div className="font-medium">提取失败</div>
            <div className="mt-0.5 text-neutral-700">{errorMsg}</div>
          </div>
        </div>
      )}
    </div>
  );
}
